import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './MobileProfileHeader.styles';
import { useMobileProfileHeader } from './useMobileProfileHeader';
import { getMobileProfileMenuItems } from '@/features/Profile/core/ProfileNav/ProfileNav.constants';
import { isProfileNavItemActive } from '@/features/Profile/core/ProfileNav/ProfileNav.helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  AngleBackIcon,
  CloseIcon,
  ExitIcon,
  MoreHorizontalIcon,
} from '@/components/UI/Icons/Icons';
import { useAuth } from '@/hooks/useAuth';

interface MobileProfileHeaderProps {
  title: string;
  currentPath?: string;
}

export function MobileProfileHeader({
  title,
  currentPath,
}: MobileProfileHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { logoutUser } = useAuth();
  const { handleBack } = useMobileProfileHeader();
  const menuItems = getMobileProfileMenuItems(t);

  function openMenu() {
    setIsMenuOpen(true);
  }

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function handleLogout() {
    logoutUser();
  }

  return (
    <S.Container>
      <S.LeftSlot>
        <S.BackButton type="button" onClick={handleBack} aria-label="Back">
          <AngleBackIcon width={12} fill={theme.palette.primary.labels} />
        </S.BackButton>
      </S.LeftSlot>

      <S.Title>{title}</S.Title>

      <S.RightSlot>
        <S.MenuButton
          type="button"
          onClick={openMenu}
          aria-label="Open profile menu"
        >
          <S.MenuButtonIconOutline>
            <MoreHorizontalIcon width={12} fill="currentColor" />
          </S.MenuButtonIconOutline>
        </S.MenuButton>
      </S.RightSlot>

      <Drawer
        isOpen={isMenuOpen}
        close={closeMenu}
        title={t('common.menu')}
        left={{ icon: <CloseIcon />, onClick: closeMenu }}
        background={theme.palette.primary.background}
        hasBottomMargin={false}
        hasBottomBorder={false}
        isInPortal
      >
        <S.MenuContent>
          <S.MenuGrid aria-label="Profile menu">
            {menuItems.map(item => (
              <S.MenuItem
                key={item.href}
                href={item.href}
                $isActive={isProfileNavItemActive(currentPath ?? '', item.href)}
                onClick={closeMenu}
              >
                {item.icon}
                <S.MenuItemTitle>{item.title}</S.MenuItemTitle>
              </S.MenuItem>
            ))}
          </S.MenuGrid>

          <S.MenuDivider />

          <S.MenuLogoutButton type="button" onClick={handleLogout}>
            <ExitIcon />
            <S.MenuItemTitle>{t('common.exit')}</S.MenuItemTitle>
          </S.MenuLogoutButton>
        </S.MenuContent>
      </Drawer>
    </S.Container>
  );
}
